import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Copy, RefreshCcw, ExternalLink, Link as LinkIcon, ShieldOff, Sparkles, Upload, FileText, Check, AlertTriangle, Mail, TrendingUp, Trophy } from "lucide-react";

// -- helpers -----------------------------------------------------------------

/**
 * Minimal RFC-4180-lite CSV parser. Handles quoted fields, embedded commas,
 * and escaped quotes. Good enough for team-invite CSV — we don't need pandas.
 */
function parseCSV(text) {
  const rows = [];
  let cur = [""], inQuotes = false, i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { cur[cur.length - 1] += '"'; i += 2; continue; }
      if (c === '"') { inQuotes = false; i++; continue; }
      cur[cur.length - 1] += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { cur.push(""); i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { rows.push(cur); cur = [""]; i++; continue; }
    cur[cur.length - 1] += c; i++;
  }
  if (cur.length > 1 || cur[0] !== "") rows.push(cur);
  if (rows.length === 0) return { headers: [], rows: [] };
  const headers = rows[0].map(h => h.trim().toLowerCase().replace(/\s+/g, "_"));
  const dataRows = rows.slice(1).filter(r => r.some(v => v && v.trim() !== "")).map(r => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (r[idx] || "").trim(); });
    return obj;
  });
  return { headers, rows: dataRows };
}

const SAMPLE_CSV = `admin_email,admin_name,business_name,preferred_subdomain,primary_color,contact_phone
alice@example.com,Alice Smith,Alice Tax Prep,alice,#2E4FB8,555-000-1111
bob@example.com,Bob Jones,Bob's 1040 Shop,bob-tax,#8b3aff,
`;

// -- component ---------------------------------------------------------------

export default function AdminTeam() {
  const [invite, setInvite] = useState(null);
  const [members, setMembers] = useState([]);
  const [rootDomain, setRootDomain] = useState("dynamictax.online");
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [csvRows, setCsvRows] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [bulking, setBulking] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const csvInputRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [i, m] = await Promise.all([
        api.get("/admin/team-invites"),
        api.get("/admin/team"),
      ]);
      const active = (i.data.invites || []).find(x => !x.revoked_at) || null;
      setInvite(active);
      setMembers(m.data.members || []);
      setRootDomain(m.data.root_domain || "dynamictax.online");
    } catch (e) {
      toast.error("Could not load team data");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const mint = async () => {
    setMinting(true);
    try {
      const r = await api.post("/admin/team-invites");
      setInvite(r.data);
      toast.success("Your team invite link is ready.");
    } catch { toast.error("Could not mint invite"); }
    finally { setMinting(false); }
  };

  const revoke = async () => {
    if (!invite) return;
    if (!window.confirm("Revoke this invite? Anyone who already redeemed keeps their site — but no one new can sign up until you mint a fresh link.")) return;
    try {
      await api.post(`/admin/team-invites/${invite.code}/revoke`);
      toast.success("Link revoked.");
      loadAll();
    } catch { toast.error("Revoke failed"); }
  };

  const inviteUrl = invite ? `${window.location.origin}/team-invite/${invite.code}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteUrl); toast.success("Invite link copied."); }
    catch { toast.error("Copy failed — highlight the link above and copy manually."); }
  };

  const handleCsvFile = async (files) => {
    if (!files?.length) return;
    setBulkResults(null);
    try {
      const text = await files[0].text();
      const parsed = parseCSV(text);
      if (!parsed.rows.length) { toast.error("CSV has no data rows."); return; }
      const required = ["admin_email", "admin_name", "business_name"];
      const missing = required.filter(r => !parsed.headers.includes(r));
      if (missing.length) { toast.error(`CSV is missing required column(s): ${missing.join(", ")}`); return; }
      setCsvHeaders(parsed.headers);
      setCsvRows(parsed.rows);
      toast.success(`Parsed ${parsed.rows.length} row${parsed.rows.length === 1 ? "" : "s"} — review then click Invite.`);
    } catch (e) {
      toast.error("Could not read that CSV file.");
    }
  };

  const runBulk = async () => {
    if (!csvRows.length) return;
    setBulking(true);
    setBulkResults(null);
    try {
      const r = await api.post("/admin/team/bulk-invite", { rows: csvRows });
      setBulkResults(r.data);
      toast.success(`${r.data.summary.invited} invited · ${r.data.summary.skipped} skipped`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    } finally { setBulking(false); }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "team-invite-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const clearCsv = () => {
    setCsvRows([]); setCsvHeaders([]); setBulkResults(null);
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12 space-y-8" data-testid="admin-team-page">
        <div>
          <div className="label-caps mb-2 flex items-center gap-2"><Users size={14} /> Your team</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-3">Team members.</h1>
          <p className="text-muted-foreground">
            Share the invite link below with your workers — each one gets their own branded tax practice site + full LMS access, on the house. Credentials are emailed to them automatically.
          </p>
        </div>

        {/* Master invite card */}
        <Card className="p-6 border-2 border-primary/30 bg-primary/5" data-testid="admin-team-invite-card">
          <div className="flex items-center gap-2 text-primary mb-2">
            <Sparkles size={14} />
            <span className="label-caps text-[10px]">Your master invite link</span>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-4">Loading…</div>
          ) : invite ? (
            <>
              <div className="rounded-md bg-background border border-border p-3 mb-4">
                <div className="font-mono text-xs sm:text-sm break-all text-foreground" data-testid="admin-team-invite-url">{inviteUrl}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={copyLink} data-testid="admin-team-copy">
                  <Copy size={14} className="mr-1" /> Copy link
                </Button>
                <Button variant="outline" asChild>
                  <a href={inviteUrl} target="_blank" rel="noreferrer">
                    <ExternalLink size={14} className="mr-1" /> Preview
                  </a>
                </Button>
                <Button variant="outline" onClick={revoke} className="text-destructive" data-testid="admin-team-revoke">
                  <ShieldOff size={14} className="mr-1" /> Revoke
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground mt-3 flex items-center gap-2">
                <LinkIcon size={11} />
                Reusable · no expiry · no cap · revoke any time
              </div>
            </>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-4">You don't have an active invite link yet.</p>
              <Button onClick={mint} disabled={minting} data-testid="admin-team-mint">
                <RefreshCcw size={14} className="mr-1" /> {minting ? "Minting…" : "Create invite link"}
              </Button>
            </div>
          )}
        </Card>

        {/* Bulk CSV upload */}
        <Card className="p-6" data-testid="admin-team-bulk-card">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={16} className="text-primary" />
            <h2 className="font-serif text-2xl">Bulk invite from CSV</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Upload a CSV of your workers and auto-provision every branded site in one click. Each worker gets an email with their login the moment their site is created.
          </p>
          <div className="mb-4 rounded-md bg-secondary/40 border border-border p-3 text-xs">
            <div className="font-medium mb-1">Required columns:</div>
            <code className="text-foreground">admin_email, admin_name, business_name</code>
            <div className="font-medium mt-2 mb-1">Optional columns:</div>
            <code className="text-foreground">preferred_subdomain, primary_color, contact_phone, logo_url</code>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <label>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={e => handleCsvFile(e.target.files)}
                data-testid="admin-team-csv-input"
              />
              <Button asChild variant="outline">
                <span><Upload size={14} className="mr-1" /> Choose CSV file</span>
              </Button>
            </label>
            <Button variant="ghost" onClick={downloadSample} data-testid="admin-team-csv-sample">
              Download template
            </Button>
            {csvRows.length > 0 && (
              <Button variant="ghost" onClick={clearCsv}>Clear</Button>
            )}
          </div>

          {csvRows.length > 0 && (
            <div className="rounded-md border border-border overflow-hidden mb-4" data-testid="admin-team-csv-preview">
              <div className="bg-secondary/40 p-3 text-xs font-medium flex items-center justify-between">
                <span>{csvRows.length} row{csvRows.length === 1 ? "" : "s"} parsed · showing first 5</span>
                <Button size="sm" onClick={runBulk} disabled={bulking || !invite} data-testid="admin-team-bulk-submit">
                  {bulking ? "Inviting…" : `Invite ${csvRows.length} worker${csvRows.length === 1 ? "" : "s"}`}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-background">
                    <tr>
                      {csvHeaders.map(h => <th key={h} className="text-left px-3 py-2 font-medium border-b border-border">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        {csvHeaders.map(h => <td key={h} className="px-3 py-2 font-mono">{r[h] || <span className="text-muted-foreground">—</span>}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {bulkResults && (
            <div className="space-y-3" data-testid="admin-team-bulk-results">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="flex items-center gap-1 text-primary">
                  <Check size={14} /> <strong>{bulkResults.summary.invited}</strong> invited
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <AlertTriangle size={14} /> <strong>{bulkResults.summary.skipped}</strong> skipped
                </span>
                {bulkResults.summary.errored > 0 && (
                  <span className="flex items-center gap-1 text-destructive">
                    <AlertTriangle size={14} /> <strong>{bulkResults.summary.errored}</strong> errored
                  </span>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/40 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 border-b border-border">Email</th>
                      <th className="text-left px-3 py-2 border-b border-border">Status</th>
                      <th className="text-left px-3 py-2 border-b border-border">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResults.results.map((r, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 font-mono">{r.admin_email}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] label-caps ${
                            r.status === "invited" ? "bg-primary/15 text-primary" :
                            r.status === "error" ? "bg-destructive/15 text-destructive" :
                            "bg-secondary text-muted-foreground"
                          }`}>{r.status}</span>
                        </td>
                        <td className="px-3 py-2">
                          {r.status === "invited" ? (
                            <div className="flex flex-wrap gap-2 items-center">
                              <a href={r.branded_url} target="_blank" rel="noreferrer" className="text-primary underline font-mono text-[11px] break-all">{r.branded_url}</a>
                              {r.email_sent ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-primary"><Mail size={10} /> emailed</span>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">password: <span className="font-mono">{r.temp_password}</span></span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-[11px]">{r.reason || "—"}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>

        {/* Team members table */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-serif text-2xl flex items-center gap-2">
              <TrendingUp size={18} className="text-primary" />
              Active workers ({members.length})
            </h2>
            <div className="text-[11px] text-muted-foreground">
              Sorted by returns filed this month · {new Date().toLocaleString('en-US', { month: 'long' })}
            </div>
          </div>
          {members.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground text-sm">
              No workers yet. Share your invite link above to get started.
            </Card>
          ) : (
            <div className="space-y-2" data-testid="admin-team-members-list">
              {members.map((m, i) => {
                const a = m.activity || { intakes_month: 0, amendments_month: 0, total_month: 0 };
                const isTop = i === 0 && a.total_month > 0;
                return (
                  <Card key={m.id} className={`p-4 flex flex-wrap items-center gap-4 ${isTop ? "border-primary/40 bg-primary/5" : ""}`} data-testid={`admin-team-member-${m.id}`}>
                    <div className="h-10 w-10 rounded-md grid place-items-center text-white font-serif shrink-0 relative" style={{ backgroundColor: m.primary_color || "#2E4FB8" }}>
                      {m.logo_url ? <img src={m.logo_url} alt="" className="h-full w-full object-contain rounded-md bg-white p-0.5" /> : (m.brand_name || "?")[0]}
                      {isTop && <Trophy size={14} className="absolute -top-2 -right-2 text-primary bg-background rounded-full p-0.5" />}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-serif text-lg leading-tight flex items-center gap-2">
                        {m.brand_name}
                        {isTop && <span className="text-[10px] px-2 py-0.5 rounded bg-primary text-primary-foreground label-caps">Top this month</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {m.admin?.name || "—"} · <span className="font-mono">{m.admin?.email || "—"}</span>
                      </div>
                    </div>

                    {/* Monthly activity badge */}
                    <div
                      className={`rounded-md px-3 py-2 text-xs shrink-0 border ${a.total_month > 0 ? "border-primary/30 bg-primary/10" : "border-border bg-secondary/40"}`}
                      title={`${a.intakes_month} intakes + ${a.amendments_month} amendments this month`}
                      data-testid={`admin-team-activity-${m.id}`}
                    >
                      <div className={`font-serif text-xl leading-none ${a.total_month > 0 ? "text-primary" : "text-muted-foreground"}`}>
                        {a.total_month}
                      </div>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-widest mt-0.5">this month</div>
                    </div>

                    <a href={`https://${m.custom_domain || `${m.subdomain}.${rootDomain}`}`} target="_blank" rel="noreferrer" className="text-xs text-primary underline font-mono break-all">
                      {m.custom_domain || `${m.subdomain}.${rootDomain}`}
                    </a>
                    <div className="text-[10px] text-muted-foreground shrink-0">
                      Joined {new Date(m.created_at).toLocaleDateString()}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-xs text-muted-foreground text-center">
          Need to remove someone? Go to <Link to="/admin/tenants" className="underline text-foreground">All tenants</Link> and set their status to <code>suspended</code>.
        </div>
      </div>
    </Layout>
  );
}
